import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Modal,
  Form,
  Select,
  Row,
  Col,
  Typography,
  Statistic,
  Tabs,
  DatePicker,
  InputNumber,
  Alert,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  SearchOutlined,
  BankOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import bankAccountService, { type BankAccountDto } from '@/services/bankAccount.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { AED_CURRENCY_CODE } from '@/constants/uaDirhamDisplay';
import { AedDirhamSign } from '@/components/currency/AedDirhamSign';
import { App } from 'antd';
import { getFinanceApiErrorMessage } from '@/utils/financeApiError';
import { getLedger } from '@/services/financialReports.service';

const { Title, Text } = Typography;
const { Search } = Input;

interface BankAccount {
  id: string;
  key: string;
  accountName: string;
  accountNumber: string;
  bankName: string;
  branch: string;
  currency: string;
  balance: number;
  accountType: string;
  status: 'Active' | 'Inactive' | 'Closed';
  iban?: string;
  swiftCode?: string;
  /** When true, row is from /finance/bank-accounts and can be edited in the register */
  isMaster?: boolean;
}

interface BankTransaction {
  key: string;
  date: string;
  reference: string;
  voucherType: string;
  description: string;
  debit: number;
  credit: number;
  balance: number;
}

export const BankAccountsPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { message } = App.useApp();
  const { allCurrencies, baseCurrency, formatCurrency } = useCurrency();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<BankAccount | null>(null);
  const [form] = Form.useForm();

  const { data: ledgerBankAccounts = [] } = useQuery({
    queryKey: ['finance', 'ledger-bank-accounts'],
    queryFn: async () => {
      const res = await api.get('/finance/reports/ledger/bank-accounts');
      const r = res as { data?: unknown };
      const inner = (r?.data as { data?: unknown })?.data ?? r?.data;
      return Array.isArray(inner) ? inner : [];
    },
    staleTime: 60_000,
  });

  const { data: bankAccountDtos = [] } = useQuery({
    queryKey: ['finance-bank-accounts'],
    queryFn: () => bankAccountService.list(),
    staleTime: 30_000,
  });

  const accountsFromGl: BankAccount[] = useMemo(
    () =>
      (ledgerBankAccounts as { id: string; account_name: string }[]).map((row) => ({
        id: row.id,
        key: row.id,
        accountName: row.account_name,
        accountNumber: '—',
        bankName: t('chartOfAccountsSource'),
        branch: '—',
        currency: baseCurrency?.currency_code ?? 'AED',
        balance: 0,
        accountType: t('accountTypeBankCashGl'),
        status: 'Active' as const,
        isMaster: false,
      })),
    [ledgerBankAccounts, baseCurrency?.currency_code, t]
  );

  const apiRows: BankAccount[] = useMemo(
    () =>
      (bankAccountDtos as BankAccountDto[]).map((b) => ({
        id: b.id,
        key: b.id,
        accountName: b.account_name,
        accountNumber: b.account_number,
        bankName: b.bank_name,
        branch: b.branch_name || '—',
        currency: b.currency,
        balance: Number(b.current_balance),
        accountType: t('accountTypeBankAccount'),
        status: (b.is_active ? 'Active' : 'Inactive') as 'Active' | 'Inactive' | 'Closed',
        iban: b.iban ?? undefined,
        swiftCode: b.swift_code ?? undefined,
        isMaster: true,
      })),
    [bankAccountDtos, t]
  );

  const displayAccounts = useMemo(() => {
    if (apiRows.length > 0) {
      return apiRows;
    }
    return accountsFromGl;
  }, [apiRows, accountsFromGl]);

  const [ledgerRange, setLedgerRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>(() => [dayjs().startOf('year'), dayjs()]);

  const { data: ledger, isFetching: ledgerLoading } = useQuery({
    queryKey: ['finance', 'bank-account-ledger', selectedAccount?.id, selectedAccount?.isMaster, ledgerRange[0].format('YYYY-MM-DD'), ledgerRange[1].format('YYYY-MM-DD')],
    queryFn: () =>
      getLedger({
        ...(selectedAccount!.isMaster
          ? { ledger_type: 'bank' as const, bank_account_id: selectedAccount!.id }
          : { ledger_type: 'account' as const, account_id: selectedAccount!.id }),
        from_date: ledgerRange[0].format('YYYY-MM-DD'),
        to_date: ledgerRange[1].format('YYYY-MM-DD'),
      }),
    enabled: !!selectedAccount,
  });

  const transactions: BankTransaction[] = useMemo(
    () =>
      (ledger?.lines ?? []).map((l, i) => ({
        key: `${l.voucher_id}-${i}`,
        date: l.voucher_date,
        reference: l.voucher_number,
        voucherType: l.voucher_type,
        description: [l.description, l.reference_number].filter(Boolean).join(' · '),
        debit: Number(l.debit_amount) || 0,
        credit: Number(l.credit_amount) || 0,
        balance: Number(l.running_balance) || 0,
      })),
    [ledger]
  );

  const statusLabel = (status: string) => {
    if (status === 'Active') return t('statusActive');
    if (status === 'Inactive') return t('statusInactive');
    if (status === 'Closed') return t('statusClosed');
    return status;
  };

  // Bank accounts table columns
  const accountColumns: ColumnsType<BankAccount> = [
    {
      title: t('accountName'),
      dataIndex: 'accountName',
      key: 'accountName',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.accountNumber}
          </Text>
        </Space>
      ),
    },
    {
      title: t('bankDetails'),
      key: 'bankDetails',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.bankName}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.branch}
          </Text>
        </Space>
      ),
    },
    {
      title: t('type'),
      dataIndex: 'accountType',
      key: 'accountType',
      width: 150,
      render: (type) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: t('currency'),
      dataIndex: 'currency',
      key: 'currency',
      width: 100,
      align: 'center',
    },
    {
      title: t('balance'),
      dataIndex: 'balance',
      key: 'balance',
      width: 150,
      align: 'right',
      render: (balance, record) => (
        <Text strong style={{ color: balance >= 0 ? '#52c41a' : '#f5222d' }}>
          {record.currency} {balance.toLocaleString()}
        </Text>
      ),
      sorter: (a, b) => a.balance - b.balance,
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const colors: { [key: string]: string } = {
          Active: 'success',
          Inactive: 'warning',
          Closed: 'default',
        };
        return <Tag color={colors[status]}>{statusLabel(status)}</Tag>;
      },
      filters: [
        { text: t('statusActive'), value: 'Active' },
        { text: t('statusInactive'), value: 'Inactive' },
        { text: t('statusClosed'), value: 'Closed' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<BankOutlined />}
            onClick={() => handleViewTransactions(record)}
          >
            {t('transactions')}
          </Button>
          {record.isMaster && (
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => void handleEdit(record)}>
              {tc('edit')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // Transactions table columns
  const transactionColumns: ColumnsType<BankTransaction> = [
    {
      title: t('date'),
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date) => dayjs(date).format('DD-MMM-YYYY'),
      sorter: (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    },
    {
      title: t('voucherCol'),
      dataIndex: 'reference',
      key: 'reference',
      width: 170,
    },
    {
      title: t('type'),
      dataIndex: 'voucherType',
      key: 'voucherType',
      width: 120,
    },
    {
      title: t('description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('debit'),
      dataIndex: 'debit',
      key: 'debit',
      width: 120,
      align: 'right',
      render: (amount) => amount > 0 ? formatCurrency(amount) : '-',
    },
    {
      title: t('credit'),
      dataIndex: 'credit',
      key: 'credit',
      width: 120,
      align: 'right',
      render: (amount) => amount > 0 ? formatCurrency(amount) : '-',
    },
    {
      title: t('balance'),
      dataIndex: 'balance',
      key: 'balance',
      width: 120,
      align: 'right',
      render: (balance) => <Text strong>{formatCurrency(balance)}</Text>,
    },
  ];

  const handleViewTransactions = (account: BankAccount) => {
    setSelectedAccount(account);
  };

  const handleEdit = async (account: BankAccount) => {
    if (!account.isMaster) {
      message.info(t('glLinkedViewInfo'));
      return;
    }
    setEditingId(account.id);
    try {
      const b = await bankAccountService.getById(account.id);
      form.setFieldsValue({
        account_name: b.account_name,
        account_number: b.account_number,
        bank_name: b.bank_name,
        branch_name: b.branch_name || '',
        currency: b.currency,
        opening_balance: b.opening_balance,
        current_balance: b.current_balance,
        iban: b.iban || '',
        swift_code: b.swift_code || '',
        status: b.is_active ? 'Active' : 'Inactive',
      });
    } catch {
      form.setFieldsValue({
        account_name: account.accountName,
        account_number: account.accountNumber,
        bank_name: account.bankName,
        branch_name: account.branch === '—' ? '' : account.branch,
        currency: account.currency,
        opening_balance: account.balance,
        current_balance: account.balance,
        iban: account.iban || '',
        swift_code: account.swiftCode || '',
        status: account.status,
      });
    }
    setIsModalVisible(true);
  };

  const handleAdd = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      currency: baseCurrency?.currency_code ?? 'AED',
      status: 'Active',
    });
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const v = await form.validateFields();
      if (editingId) {
        await bankAccountService.update(editingId, {
          account_name: v.account_name,
          account_number: v.account_number,
          bank_name: v.bank_name,
          branch_name: v.branch_name || null,
          currency: v.currency,
          opening_balance: v.opening_balance != null ? Number(v.opening_balance) : undefined,
          current_balance: v.current_balance != null ? Number(v.current_balance) : undefined,
          iban: v.iban || null,
          swift_code: v.swift_code || null,
          is_active: v.status === 'Active',
        });
        message.success(t('bankAccountUpdated'));
      } else {
        await bankAccountService.create({
          account_name: v.account_name,
          account_number: v.account_number,
          bank_name: v.bank_name,
          branch_name: v.branch_name || undefined,
          currency: v.currency,
          opening_balance: v.opening_balance != null ? Number(v.opening_balance) : 0,
          iban: v.iban,
          swift_code: v.swift_code,
          is_active: v.status === 'Active',
        });
        message.success(t('bankAccountCreated'));
      }
      await queryClient.invalidateQueries({ queryKey: ['finance-bank-accounts'] });
      setIsModalVisible(false);
      form.resetFields();
      setEditingId(null);
    } catch (e: unknown) {
      if ((e as { errorFields?: unknown })?.errorFields) {
        return;
      }
      message.error(getFinanceApiErrorMessage(e, t('errSaveBankAccount')));
    }
  };

  // Calculate total balance
  const totalBalance = displayAccounts
    .filter((acc) => acc.status === 'Active')
    .reduce((sum, acc) => sum + acc.balance, 0);

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>{t('bankAccounts')}</Title>
        <Text type="secondary">
          {t('bankAccountsSubtitle')}
        </Text>
      </div>

      {apiRows.length > 0 ? null : accountsFromGl.length > 0 ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('noBankRegisterShowingGl')}
        />
      ) : null}

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title={t('totalBalance')}
              value={totalBalance}
              prefix={
                String(baseCurrency?.currency_code || '').toUpperCase() === AED_CURRENCY_CODE ? (
                  <AedDirhamSign />
                ) : (
                  (baseCurrency?.symbol ?? baseCurrency?.currency_code ?? '')
                )
              }
              valueStyle={{ color: '#52c41a' }}
              suffix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title={t('activeAccounts')}
              value={displayAccounts.filter((a) => a.status === 'Active').length}
              prefix={<BankOutlined />}
              valueStyle={{ color: '#145A54' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Card with Tabs */}
      <Card>
        <Tabs
          defaultActiveKey="accounts"
          tabBarExtraContent={
            selectedAccount ? (
              <Button onClick={() => setSelectedAccount(null)}>
                {t('backToAccounts')}
              </Button>
            ) : (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                {t('addBankAccount')}
              </Button>
            )
          }
        >
          <Tabs.TabPane tab={t('bankAccountsTab')} key="accounts">
            {/* Toolbar */}
            <div style={{ marginBottom: '16px' }}>
              <Search
                placeholder={t('searchBankAccounts')}
                allowClear
                style={{ width: 300 }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                prefix={<SearchOutlined />}
              />
            </div>

            {/* Accounts Table */}
            {!selectedAccount ? (
              <Table
                columns={accountColumns}
                dataSource={displayAccounts}
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) => t('totalAccounts', { count: total }),
                }}
                size="small"
              />
            ) : (
              <div>
                <Card style={{ marginBottom: '16px', backgroundColor: '#fafafa' }}>
                  <Row gutter={16}>
                    <Col xs={24} md={12} xl={8}>
                      <Text strong>{t('accountColon')}</Text> {selectedAccount.accountName}
                    </Col>
                    <Col xs={24} md={12} xl={8}>
                      <Text strong>{t('bankColon')}</Text> {selectedAccount.bankName}
                    </Col>
                    <Col xs={24} md={12} xl={8}>
                      <Text strong>{t('balanceColon')}</Text>{' '}
                      <Text strong style={{ color: '#52c41a' }}>
                        {selectedAccount.currency} {selectedAccount.balance.toLocaleString()}
                      </Text>
                    </Col>
                  </Row>
                </Card>

                <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <DatePicker.RangePicker
                    allowClear={false}
                    value={ledgerRange}
                    onChange={(v) => {
                      if (v?.[0] && v?.[1]) setLedgerRange([v[0], v[1]]);
                    }}
                  />
                  {ledger ? (
                    <Text type="secondary">
                      {t('openingClosingBalances', {
                        opening: formatCurrency(Number(ledger.opening_balance) || 0),
                        closing: formatCurrency(Number(ledger.closing_balance) || 0),
                      })}
                    </Text>
                  ) : null}
                </div>

                <Table
                  columns={transactionColumns}
                  dataSource={transactions}
                  loading={ledgerLoading}
                  locale={{ emptyText: t('noPostingsInPeriod') }}
                  pagination={{
                    defaultPageSize: 20,
                    showSizeChanger: true,
                    showTotal: (total) => t('totalTransactions', { count: total }),
                  }}
                  size="small"
                />
              </div>
            )}
          </Tabs.TabPane>

          <Tabs.TabPane tab={t('reconciliationTab')} key="reconciliation">
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <Text type="secondary" style={{ fontSize: '16px', display: 'block', marginBottom: 12 }}>
                {t('bankReconciliationComingSoon')}
              </Text>
              <Text type="secondary">{t('bankReconciliationBlockedHint')}</Text>
            </div>
          </Tabs.TabPane>
        </Tabs>
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingId ? t('editBankAccount') : t('addBankAccount')}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setEditingId(null);
        }}
        width={700}
        okText={tc('save')}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} lg={12}>
              <Form.Item
                name="account_name"
                label={t('accountName')}
                rules={[{ required: true, message: t('enterAccountName') }]}
              >
                <Input placeholder={t('accountNamePlaceholder')} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="account_number"
                label={t('accountNumber')}
                rules={[{ required: true, message: t('enterAccountNumber') }]}
              >
                <Input placeholder={t('accountNumberPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} lg={12}>
              <Form.Item
                name="bank_name"
                label={t('bankNameLabel')}
                rules={[{ required: true, message: t('enterBankName') }]}
              >
                <Input placeholder={t('bankNamePlaceholder')} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item name="branch_name" label={t('branch')}>
                <Input placeholder={t('branchPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12} xl={8}>
              <Form.Item
                name="currency"
                label={t('currency')}
                rules={[{ required: true, message: t('selectCurrency') }]}
                initialValue={baseCurrency?.currency_code}
              >
                <Select
                  placeholder={t('selectCurrency')}
                  showSearch
                  optionFilterProp="children"
                >
                  {allCurrencies.map((currency) => (
                    <Select.Option key={currency.id} value={currency.currency_code}>
                      {currency.currency_code} - {currency.currency_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name="opening_balance" label={t('openingBalance')}>
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item
                name="current_balance"
                label={t('currentBalance')}
                extra={editingId ? t('currentBalanceEditExtra') : t('currentBalanceCreateExtra')}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  disabled={!editingId}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} lg={12}>
              <Form.Item name="iban" label={t('iban')}>
                <Input placeholder={t('ibanPlaceholder')} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item name="swift_code" label={t('swiftCode')}>
                <Input placeholder={t('swiftPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="status" label={t('status')} initialValue="Active">
            <Select showSearch optionFilterProp="children">
              <Select.Option value="Active">{t('statusActive')}</Select.Option>
              <Select.Option value="Inactive">{t('statusInactive')}</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
